import React from 'react';

import {
  type ArtifactMetricValuesQuery,
  useArtifactMetricSearchQuery,
  useArtifactMetricValuesQuery,
} from '../../generated/graphql';
import {logError} from '../../services/errors/errorReporting';
import {PageViews} from '../../util/datadog/artifacts/pageViews';
import {type PerformanceMetricsContext} from '../../util/datadog/artifacts/trackPageMetric';
import {useDataFetchTiming} from '../../util/datadog/artifacts/useDataFetchTiming';
import {propagateErrorsContext} from '../../util/errors';
import {type CollectionOverviewAnalyticsContextValue} from '../ArtifactOverview/collectionOverviewAnalytics';
import {compareArtifactVersionMetricRowsByVersion} from './artifactVersionMetricChartUtils';
import type {ArtifactVersionMetricChartRow} from './types';

type ArtifactMetricValueRow = Extract<
  NonNullable<ArtifactMetricValuesQuery['artifactCollection']>,
  {__typename: 'ArtifactPortfolio'}
>['artifactMetricValues'][number];

export interface ArtifactVersionMetricDataState {
  rows: ArtifactVersionMetricChartRow[];
  loading: boolean;
  error?: Error;
  retry?: () => void;
  hasPartialData: boolean;
  hasNoData: boolean;
  dataMin: number | null;
  dataMax: number | null;
}

type PreviousArtifactMetricRowsCache = {
  key: string | null;
  rows: ArtifactVersionMetricChartRow[];
};

export function hasArtifactVersionMetricName(
  metricName?: string
): metricName is string {
  return metricName != null && metricName !== '';
}

export function mapArtifactMetricValuesToChartRows(
  membershipIds: string[],
  metricRows: ArtifactMetricValueRow[]
): ArtifactVersionMetricChartRow[] {
  const selectionIndexByMembershipId = new Map<string, number>();
  membershipIds.forEach((membershipId, index) => {
    selectionIndexByMembershipId.set(membershipId, index);
  });

  const rowsByMembershipId = new Map<string, ArtifactVersionMetricChartRow>();
  for (const row of metricRows) {
    const selectionIndex = selectionIndexByMembershipId.get(row.membershipId);
    if (selectionIndex == null || rowsByMembershipId.has(row.membershipId)) {
      continue;
    }

    const value =
      typeof row.value === 'number' && Number.isFinite(row.value)
        ? row.value
        : null;

    rowsByMembershipId.set(row.membershipId, {
      membershipId: row.membershipId,
      selectionIndex,
      label: getArtifactVersionMetricLabel(row, selectionIndex),
      value,
      versionIndex: row.versionIndex,
      aliases: row.aliases,
      historyStep: row.historyStep ?? null,
      sourceRunName: row.sourceRunName ?? null,
      sourceProjectName: row.sourceProjectName ?? null,
    });
  }

  return [...rowsByMembershipId.values()].sort(
    compareArtifactVersionMetricRowsByVersion
  );
}

function getArtifactMetricRowsCacheKey({
  artifactCollectionId,
  metricName,
}: {
  artifactCollectionId?: string;
  metricName?: string;
}) {
  return `${artifactCollectionId ?? ''}:${metricName ?? ''}`;
}

export function getArtifactVersionMetricRowsForRender({
  cache,
  key,
  membershipIds,
  rows,
  loading,
}: {
  cache: PreviousArtifactMetricRowsCache;
  key: string;
  membershipIds: string[];
  rows: ArtifactVersionMetricChartRow[];
  loading: boolean;
}) {
  if (loading && cache.key === key && cache.rows.length > 0) {
    const selectedMembershipIds = new Set(membershipIds);
    return cache.rows
      .filter(row => selectedMembershipIds.has(row.membershipId))
      .map(row => ({
        ...row,
        selectionIndex: membershipIds.indexOf(row.membershipId),
      }))
      .filter(row => row.selectionIndex >= 0)
      .sort(compareArtifactVersionMetricRowsByVersion);
  }

  return rows;
}

function getNumericArtifactVersionMetricValues(
  rows: ArtifactVersionMetricChartRow[]
) {
  return rows
    .map(row => row.value)
    .filter(
      (value): value is number => value != null && Number.isFinite(value)
    );
}

export function deriveArtifactVersionMetricDataState({
  membershipIds,
  metricName,
  rows,
  loading,
  error,
  retry,
}: {
  membershipIds: string[];
  metricName?: string;
  rows: ArtifactVersionMetricChartRow[];
  loading: boolean;
  error?: Error;
  retry?: () => void;
}): ArtifactVersionMetricDataState {
  const hasMetric = hasArtifactVersionMetricName(metricName);
  const values = getNumericArtifactVersionMetricValues(rows);
  return {
    rows,
    loading,
    error,
    retry,
    hasPartialData:
      !loading &&
      error == null &&
      hasMetric &&
      membershipIds.length > 0 &&
      values.length > 0 &&
      values.length < membershipIds.length,
    hasNoData:
      !loading &&
      error == null &&
      hasMetric &&
      membershipIds.length > 0 &&
      values.length === 0,
    dataMin: values.length > 0 ? Math.min(...values) : null,
    dataMax: values.length > 0 ? Math.max(...values) : null,
  };
}

export function useArtifactVersionMetricSearch({
  artifactCollectionId,
  analyticsContext,
  membershipIds,
  metricNamePattern,
  first = 50,
  enabled = true,
}: {
  artifactCollectionId?: string;
  analyticsContext?: CollectionOverviewAnalyticsContextValue;
  membershipIds: string[];
  metricNamePattern?: string;
  first?: number;
  enabled?: boolean;
}) {
  const skip = !enabled || !artifactCollectionId || membershipIds.length === 0;
  const query = useArtifactMetricSearchQuery({
    skip,
    variables: {
      artifactCollectionId: artifactCollectionId ?? '',
      membershipIds,
      metricNamePattern,
      first,
    },
    context: propagateErrorsContext(),
    onError: logError,
  });
  const artifactCollection = query.data?.artifactCollection;
  const metricNames =
    artifactCollection?.__typename === 'ArtifactPortfolio'
      ? artifactCollection.artifactMetricSearch.map(result => result.name)
      : [];
  const metricsContext: PerformanceMetricsContext = {
    pageName: PageViews.ArtifactCollectionVersionMetrics,
    artifactCollectionId,
    artifactCollectionName: analyticsContext?.artifactCollectionName,
    artifactType: analyticsContext?.artifactTypeName,
    collectionType: analyticsContext?.collectionType,
    entityName: analyticsContext?.entityName,
    projectName: analyticsContext?.projectName,
    source: analyticsContext?.source,
    first,
    hasSearchPattern: metricNamePattern?.trim() !== '',
    membershipCount: membershipIds.length,
  };
  useDataFetchTiming(
    skip ? null : metricsContext,
    query.loading,
    'artifact_version_metric_search',
    metricNames.length
  );

  return {
    metricNames,
    loading: query.loading,
    error: query.error,
  };
}

export function deriveArtifactVersionMetricPickerState({
  enabled,
  searchPattern,
  availableMetricNames,
  availableLoading,
  filteredMetricNames,
  filteredLoading,
}: {
  enabled: boolean;
  searchPattern: string;
  availableMetricNames: string[];
  availableLoading: boolean;
  filteredMetricNames: string[];
  filteredLoading: boolean;
}) {
  const isSearching = searchPattern.trim() !== '';

  return {
    metricNames: isSearching ? filteredMetricNames : availableMetricNames,
    loading: availableLoading || (isSearching && filteredLoading),
    hasNoMetricsAvailable:
      enabled && !availableLoading && availableMetricNames.length === 0,
  };
}

export function deriveSelectedArtifactVersionMetricAvailability({
  enabled,
  loading,
  membershipIds,
  metricNames,
}: {
  enabled: boolean;
  loading: boolean;
  membershipIds: string[];
  metricNames: string[];
}) {
  const hasSelectedVersions = membershipIds.length > 0;

  return {
    loading: enabled && hasSelectedVersions && loading,
    hasNoMetricsAvailable:
      enabled && hasSelectedVersions && !loading && metricNames.length === 0,
  };
}

export function useArtifactVersionMetricPickerSearch({
  artifactCollectionId,
  analyticsContext,
  membershipIds,
  searchPattern,
  enabled = true,
}: {
  artifactCollectionId?: string;
  analyticsContext?: CollectionOverviewAnalyticsContextValue;
  membershipIds: string[];
  searchPattern: string;
  enabled?: boolean;
}) {
  const trimmedPattern = searchPattern.trim();
  const isSearching = trimmedPattern !== '';

  const availableMetrics = useArtifactVersionMetricSearch({
    artifactCollectionId,
    analyticsContext,
    membershipIds,
    enabled,
  });
  const filteredMetrics = useArtifactVersionMetricSearch({
    artifactCollectionId,
    analyticsContext,
    membershipIds,
    metricNamePattern: trimmedPattern,
    enabled: enabled && isSearching,
  });

  return deriveArtifactVersionMetricPickerState({
    enabled,
    searchPattern,
    availableMetricNames: availableMetrics.metricNames,
    availableLoading: availableMetrics.loading,
    filteredMetricNames: filteredMetrics.metricNames,
    filteredLoading: filteredMetrics.loading,
  });
}

export function useArtifactVersionMetricValues({
  artifactCollectionId,
  analyticsContext,
  membershipIds,
  metricName,
  enabled = true,
}: {
  artifactCollectionId?: string;
  analyticsContext?: CollectionOverviewAnalyticsContextValue;
  membershipIds: string[];
  metricName?: string;
  enabled?: boolean;
}): ArtifactVersionMetricDataState {
  const skip =
    !enabled ||
    !artifactCollectionId ||
    membershipIds.length === 0 ||
    !hasArtifactVersionMetricName(metricName);
  const query = useArtifactMetricValuesQuery({
    skip,
    variables: {
      artifactCollectionId: artifactCollectionId ?? '',
      membershipIds,
      metricName: metricName ?? '',
    },
    context: propagateErrorsContext(),
    onError: logError,
  });
  const [rowsCache, setRowsCache] =
    React.useState<PreviousArtifactMetricRowsCache>({
      key: null,
      rows: [],
    });
  const artifactCollection = query.data?.artifactCollection;

  const rows = React.useMemo(
    () =>
      mapArtifactMetricValuesToChartRows(
        membershipIds,
        artifactCollection?.__typename === 'ArtifactPortfolio'
          ? artifactCollection.artifactMetricValues
          : []
      ),
    [artifactCollection, membershipIds]
  );
  const metricsContext: PerformanceMetricsContext = {
    pageName: PageViews.ArtifactCollectionVersionMetrics,
    artifactCollectionId,
    artifactCollectionName: analyticsContext?.artifactCollectionName,
    artifactType: analyticsContext?.artifactTypeName,
    collectionType: analyticsContext?.collectionType,
    entityName: analyticsContext?.entityName,
    projectName: analyticsContext?.projectName,
    source: analyticsContext?.source,
    membershipCount: membershipIds.length,
    metricNameConfigured: hasArtifactVersionMetricName(metricName),
  };
  useDataFetchTiming(
    skip ? null : metricsContext,
    query.loading,
    'artifact_version_metric_values',
    rows.length
  );
  const cacheKey = getArtifactMetricRowsCacheKey({
    artifactCollectionId,
    metricName,
  });
  React.useEffect(() => {
    if (!query.loading && query.error == null) {
      setRowsCache({
        key: cacheKey,
        rows,
      });
    }
  }, [cacheKey, query.error, query.loading, rows]);
  const renderRows = getArtifactVersionMetricRowsForRender({
    cache: rowsCache,
    key: cacheKey,
    membershipIds,
    rows,
    loading: query.loading,
  });
  const retry = () => {
    void query.refetch();
  };

  return deriveArtifactVersionMetricDataState({
    membershipIds,
    metricName,
    rows: renderRows,
    loading: query.loading,
    error: query.error,
    retry,
  });
}

function getArtifactVersionMetricLabel(
  row: ArtifactMetricValueRow,
  selectionIndex: number
): string {
  const versionAlias = row.aliases.find(alias => /^v\d+$/.test(alias));
  if (versionAlias != null) {
    return versionAlias;
  }
  if (row.versionIndex != null) {
    return `v${row.versionIndex}`;
  }
  if (row.aliases.length > 0) {
    return row.aliases[0];
  }
  return `Version ${selectionIndex + 1}`;
}
